import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { complianceTaskApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import clsx from 'clsx';
import { useTeamId } from '../hooks/useTeamId';

const CATEGORY_CONFIG: Record<string, { label: string; className: string }> = {
  EXAM_PREP: { label: 'Exam Prep', className: 'bg-purple-100 text-purple-700' },
  FILING_DEADLINE: { label: 'Filing Deadline', className: 'bg-red-100 text-red-700' },
  POLICY_REVIEW: { label: 'Policy Review', className: 'bg-blue-100 text-blue-700' },
  TRAINING: { label: 'Training', className: 'bg-green-100 text-green-700' },
  REMEDIATION: { label: 'Remediation', className: 'bg-orange-100 text-orange-700' },
  CONTENT_REVIEW: { label: 'Content Review', className: 'bg-yellow-100 text-yellow-700' },
  BOARD_REPORT: { label: 'Board Report', className: 'bg-indigo-100 text-indigo-700' },
  REGULATORY_CHANGE: { label: 'Reg. Change', className: 'bg-pink-100 text-pink-700' },
  OTHER: { label: 'Other', className: 'bg-gray-100 text-gray-700' },
};

const STATUS_CONFIG: Record<string, { label: string; className: string; variant: BadgeVariant }> = {
  PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-700', variant: 'neutral' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700', variant: 'info' },
  COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-700', variant: 'success' },
  OVERDUE: { label: 'Overdue', className: 'bg-red-100 text-red-700', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', className: 'bg-gray-100 text-gray-400 line-through', variant: 'draft' },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  CRITICAL: { label: 'Critical', color: 'text-red-600' },
  HIGH: { label: 'High', color: 'text-orange-600' },
  MEDIUM: { label: 'Medium', color: 'text-yellow-600' },
  LOW: { label: 'Low', color: 'text-gray-400' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const CATEGORY_FILTERS = [
  { value: '', label: 'All Categories' },
  ...Object.entries(CATEGORY_CONFIG).map(([value, { label }]) => ({ value, label })),
];

export default function ComplianceCalendarPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  // Initialize filters from the URL so deep-links like /calendar?status=OVERDUE
  // (e.g. the dashboard's "Overdue" urgent number) open pre-filtered.
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') ?? '');

  // Team
  const { teamId } = useTeamId();

  // Tasks
  const { data: tasksData, isLoading, isError, refetch } = useQuery({
    queryKey: ['complianceTasks', teamId, statusFilter, categoryFilter],
    queryFn: () => complianceTaskApi.list(teamId!, {
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
    }),
    enabled: !!teamId,
  });
  const tasks = tasksData?.data?.tasks || [];

  // Create form
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'OTHER',
    priority: 'MEDIUM',
    dueDate: '',
    recurringSchedule: '',
    reminderDays: 7,
    assignedTo: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => complianceTaskApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTasks'] });
      toast.success('Task created');
      setShowCreate(false);
      setForm({ title: '', description: '', category: 'OTHER', priority: 'MEDIUM', dueDate: '', recurringSchedule: '', reminderDays: 7, assignedTo: '' });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to create task'),
  });

  const completeMutation = useMutation({
    mutationFn: (taskId: string) => complianceTaskApi.complete(teamId!, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTasks'] });
      toast.success('Task completed');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => complianceTaskApi.cancel(teamId!, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTasks'] });
      toast.success('Task cancelled');
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => complianceTaskApi.seedRegulatory(teamId!),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['complianceTasks'] });
      toast.success(`Created ${res.data.created} regulatory tasks`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to seed calendar'),
  });

  // Summary counts
  const overdue = tasks.filter((t: any) => t.status === 'OVERDUE').length;
  const dueThisWeek = tasks.filter((t: any) => {
    if (t.status === 'COMPLETED' || t.status === 'CANCELLED') return false;
    const due = new Date(t.dueDate);
    const now = new Date();
    const weekOut = new Date();
    weekOut.setDate(weekOut.getDate() + 7);
    return due >= now && due <= weekOut;
  }).length;
  const dueThisMonth = tasks.filter((t: any) => {
    if (t.status === 'COMPLETED' || t.status === 'CANCELLED') return false;
    const due = new Date(t.dueDate);
    const now = new Date();
    const monthOut = new Date();
    monthOut.setDate(monthOut.getDate() + 30);
    return due >= now && due <= monthOut;
  }).length;
  const completedThisMonth = tasks.filter((t: any) => {
    if (t.status !== 'COMPLETED') return false;
    const completed = new Date(t.completedAt);
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    return completed >= monthAgo;
  }).length;

  return (
    <FeatureGate feature="compliance_calendar" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Calendar & Tasks"
          description="Track deadlines, filings, reviews, and compliance tasks"
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <>
                {tasks.length === 0 && (
                  <button
                    onClick={() => seedMutation.mutate()}
                    disabled={seedMutation.isPending}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {seedMutation.isPending ? 'Seeding...' : 'Seed Regulatory Calendar'}
                  </button>
                )}
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  New Task
                </button>
              </>
            </PermissionGate>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-red-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Overdue</p>
                <p className={clsx('text-2xl font-bold', overdue > 0 ? 'text-red-600' : 'text-gray-900')}>{overdue}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-50 p-2">
                <ClockIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Due This Week</p>
                <p className="text-2xl font-bold text-gray-900">{dueThisWeek}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2">
                <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Due This Month</p>
                <p className="text-2xl font-bold text-gray-900">{dueThisMonth}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-50 p-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed (30d)</p>
                <p className="text-2xl font-bold text-gray-900">{completedThisMonth}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <Listbox value={statusFilter} onChange={setStatusFilter}>
            <div className="relative w-44">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span>{STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {STATUS_FILTERS.map((f) => (
                    <Listbox.Option
                      key={f.value}
                      value={f.value}
                      className={({ active }) => clsx('relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')}
                    >
                      {({ selected }) => (
                        <>
                          <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          <Listbox value={categoryFilter} onChange={setCategoryFilter}>
            <div className="relative w-48">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span>{CATEGORY_FILTERS.find((f) => f.value === categoryFilter)?.label}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {CATEGORY_FILTERS.map((f) => (
                    <Listbox.Option
                      key={f.value}
                      value={f.value}
                      className={({ active }) => clsx('relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')}
                    >
                      {({ selected }) => (
                        <>
                          <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        {/* Task list */}
        <div className="rounded-lg border bg-white">
          {isError ? (
            <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 text-center">
              <p className="text-red-600 font-medium mb-2">Failed to load data</p>
              <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
              <button onClick={() => refetch()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
                Retry
              </button>
            </div>
          ) : isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : tasks.length === 0 ? (
            <EmptyState
              icon={CalendarDaysIcon}
              title={statusFilter || categoryFilter ? 'No tasks match these filters' : 'No tasks yet'}
              description={
                statusFilter || categoryFilter
                  ? 'Try clearing the filters, or add a task for this view.'
                  : 'Seed the regulatory calendar for standard deadlines, or add your first task.'
              }
              action={
                <PermissionGate teamId={teamId} permission="compliance.write">
                  <button
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-ocean-600 hover:text-ocean-800"
                  >
                    <PlusIcon className="w-4 h-4 mr-1.5" />
                    Add Task
                  </button>
                </PermissionGate>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-4 py-3" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map((task: any) => {
                    const cat = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.OTHER;
                    const st = STATUS_CONFIG[task.status] || STATUS_CONFIG.PENDING;
                    const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.MEDIUM;
                    const isActionable = task.status === 'PENDING' || task.status === 'IN_PROGRESS' || task.status === 'OVERDUE';
                    const dueDate = new Date(task.dueDate);
                    const isOverdue = dueDate < new Date() && isActionable;

                    return (
                      <tr key={task.id} className={clsx('hover:bg-gray-50', task.status === 'CANCELLED' && 'opacity-50')}>
                        <td className="px-4 py-3">
                          {isActionable && (
                            <button
                              onClick={() => completeMutation.mutate(task.id)}
                              className="h-5 w-5 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50"
                              title="Mark complete"
                            />
                          )}
                          {task.status === 'COMPLETED' && (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{task.title}</div>
                          {task.description && (
                            <div className="mt-0.5 text-xs text-gray-500 line-clamp-1">{task.description}</div>
                          )}
                          {task.recurringSchedule && (
                            <span className="mt-0.5 inline-flex text-xs text-gray-400">
                              Recurring: {task.recurringSchedule.toLowerCase().replace('_', '-')}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', cat.className)}>
                            {cat.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={clsx('text-xs font-medium', prio.color)}>{prio.label}</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={clsx('text-sm', isOverdue ? 'font-semibold text-red-600' : 'text-gray-500')}>
                            {dueDate.toLocaleDateString()}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <StatusBadge variant={st.variant} className={task.status === 'CANCELLED' ? 'line-through' : undefined}>
                            {st.label}
                          </StatusBadge>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {isActionable && (
                            <button
                              onClick={() => cancelMutation.mutate(task.id)}
                              className="text-xs text-gray-400 hover:text-red-500"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Task Modal */}
        <Transition appear show={showCreate} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowCreate(false)}>
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">New Compliance Task</Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Title</label>
                        <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category</label>
                          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            {Object.entries(CATEGORY_CONFIG).map(([value, { label }]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Priority</label>
                          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            <option value="CRITICAL">Critical</option>
                            <option value="HIGH">High</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="LOW">Low</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Due Date</label>
                          <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Recurring</label>
                          <select value={form.recurringSchedule} onChange={(e) => setForm({ ...form, recurringSchedule: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            <option value="">None</option>
                            <option value="MONTHLY">Monthly</option>
                            <option value="QUARTERLY">Quarterly</option>
                            <option value="SEMI_ANNUAL">Semi-Annual</option>
                            <option value="ANNUAL">Annual</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button onClick={() => setShowCreate(false)} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                      <button
                        onClick={() => createMutation.mutate({
                          title: form.title,
                          description: form.description || undefined,
                          category: form.category,
                          priority: form.priority,
                          dueDate: form.dueDate,
                          recurringSchedule: form.recurringSchedule || undefined,
                          reminderDays: form.reminderDays,
                        })}
                        disabled={!form.title || !form.dueDate || createMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {createMutation.isPending ? 'Creating...' : 'Create Task'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
